import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import compose from 'recompose/compose'
import { List, Map } from 'immutable'
import _assign from 'lodash/assign'
import { connect as connectRedux } from 'react-redux'
import _get from 'lodash/get'
import { connect as connectPage, PAGE_SEO_TYPE_LOCATION } from '../../Page/connect'
import { H1, HEADING_TYPES } from '../../Heading/index'
import PlanCardGrid from '../../PlanCardGrid/index'
import MyAccountChangePlanCard from '../MyAccountChangePlanCard/index'
import BackButton from '../../BackButton/index'
import { URL_ACCOUNT, URL_GO_PAGE } from '../../../services/url/constants'
import { getAuthIsLoggedIn } from '../../../services/auth/index'
import { getPrimary } from '../../../services/languages/index'
import {
  hasActivePause,
  isPrimaryAccountProfile,
  isGaiaDirectPayment,
  userChangePlanAllowed,
} from '../../../services/user-account/index'
import GenericMessage, { ALIGN_LEFT } from '../../GenericMessage/index'
import {
  getPlanSubscriptionType,
  PLAN_ID_COMPLIMENTARY,
  replaceSkuToPriceIncreasedSku,
  INACTIVE_SUBSCRIPTION,
  SUBSCRIPTION_NOT_FOUND,
  USER_REQUIRED,
} from '../../../services/plans/index'
import { historyRedirect } from '../../../services/navigation/index'
import {
  TYPE_LOGIN,
  TYPE_MODAL_PLAN_ACCESS,
} from '../../../services/dialog/index'
import { getBoundActions } from '../../../actions/index'
import { getCurrentTime } from '../../../services/date-time/index'
import { get as getConfig } from '../../../config/index'
import {
  BILLING_PLAN_GRID_LOCATION_CHANGE_PLAN,
} from '../../../services/plans/utils'
import {
  cleanSku,
} from '../../../services/checkout/index'
import { useGetPlanDataBySku, useGetPlanEligibility } from '../../../services/plans/hooks'
import { scrollToTop } from '../../../services/dom/index'

const config = getConfig()
const eligibilityCheckActive = !!_get(config, ['features', 'checkout', 'eligibilityCheck'])
// Immutable .get(..., Map()) defaults must be shared; inline Map() changes identity every render.
const EMPTY_PLAN_MAP = Map()

function ChangePlanCardPlaceholder () {
  return (
    <div className="my-account-change-plan__card-placeholder">
      <div className="my-account-change-plan__card-placeholder-item my-account-change-plan__card-placeholder-item--small" />
      <div className="my-account-change-plan__card-placeholder-item my-account-change-plan__card-placeholder-item--small" />
      <div className="my-account-change-plan__card-placeholder-item my-account-change-plan__card-placeholder-item--large" />
      <div className="my-account-change-plan__card-placeholder-item" />
    </div>
  )
}

function MyAccountChangePlanPage (props) {
  const {
    history,
    staticText,
    auth,
    location,
    user,
    userAccount,
    paytrack,
    userProfiles,
    setOverlayDialogVisible,
    renderModal,
    plans,
    dialog,
    removeModal,
  } = props
  const dialogName = dialog.get('componentName')
  const userLanguage = getPrimary(user.getIn(['data', 'language']))
  const authJwt = auth.get('jwt')
  const skuParam = _get(location, ['query', 'sku'])
  const userPlanSku = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan', 'planSku'])
  // remove any whitespace so it doesn't matter if there are spaces in the sku
  const cleanSkuParam = cleanSku(skuParam)
  const gqlPlanData = useGetPlanDataBySku({
    userLanguage,
    authJwt,
    planSkus: skuParam || null,
  })
  const formattedPlanData = gqlPlanData.get('plans', List()) || List()

  const gqlUserPlanData = useGetPlanDataBySku({
    userLanguage,
    authJwt,
    planSkus: skuParam ? userPlanSku : null,
    forceRefresh: true,
  }) || Map()
  const userPlanComplete = gqlUserPlanData.get('complete', false)
  const userPlanData = gqlUserPlanData.get('plans', List()) || List()
  const currentPlan = userPlanData.get(0) || Map()

  const planEligibility = useGetPlanEligibility({
    sku: skuParam || null,
    authJwt,
    page: BILLING_PLAN_GRID_LOCATION_CHANGE_PLAN,
    enabled: eligibilityCheckActive && !!skuParam,
  })
  const incomingPlan = formattedPlanData.find((plan) => cleanSku(plan.get('sku')) === cleanSkuParam) || EMPTY_PLAN_MAP
  const eligibility = eligibilityCheckActive && skuParam
    ? (planEligibility.get('eligibility') || EMPTY_PLAN_MAP)
    : EMPTY_PLAN_MAP
  const isPlanAvailable = incomingPlan.get('changePlanAllowed') && eligibility.get('eligible')

  const incomingPlanDataComplete = gqlPlanData.get('complete') && userPlanComplete
  const eligibilityDataComplete = !eligibilityCheckActive || planEligibility.get('complete')
  const skuDeepLinkContentReady = incomingPlanDataComplete && eligibilityDataComplete

  const isActivePause = hasActivePause(userAccount)
  const isPrimaryProfile = isPrimaryAccountProfile(props)
  const isUserAccountProcessing = userAccount.get('billingSubscriptionsProcessing')
  const isDirectPayment = isGaiaDirectPayment(userAccount, paytrack)
  const productRatePlanId = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan', 'productRatePlanId'])
  let userProductRatePlanId = productRatePlanId
  const changePlanSuccess = userAccount.getIn(['data', 'billing', 'planChange', 'success']) && userAccount.getIn(['data', 'billing', 'planChange', 'orderNumber'])

  if (productRatePlanId === PLAN_ID_COMPLIMENTARY) {
    userProductRatePlanId = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'nextPlan', 'productRatePlanId'])
  }

  const changePlanAllowed = userChangePlanAllowed({ userAccount })
  const accountSubscriptionDataReady = !isUserAccountProcessing && (userAccount.getIn(['details', 'data', 'billing', 'subscriptions'], Map()) || Map()).size > 0
  const userPlanSubscriptionType = getPlanSubscriptionType(userProductRatePlanId)
  const cancelDate = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'cancelDate'])
  const endDate = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'endDate'])
  const today = getCurrentTime()
  const cancellationScheduled = cancelDate && (new Date(endDate) >= today)
  const eligibilityShouldGoToCheckout = (eligibility.get('errorCode') === INACTIVE_SUBSCRIPTION || eligibility.get('errorCode') === SUBSCRIPTION_NOT_FOUND)
    || (!changePlanAllowed && accountSubscriptionDataReady)

  useEffect(() => {
    scrollToTop()
  }, [])

  useEffect(() => {
    // do not show the modal for people in this state
    // just send them to the go page
    if (skuParam && eligibilityCheckActive && eligibilityShouldGoToCheckout) {
      historyRedirect({
        url: `${URL_GO_PAGE}?sku=${skuParam}`,
        history,
        auth,
        language: userLanguage,
      })
    }
  }, [skuParam, eligibilityCheckActive, eligibilityShouldGoToCheckout])

  // Plan access modal: wait for plan-by-SKU + current-plan GraphQL and GET_PLAN_ELIGIBILITY
  // (skuDeepLinkContentReady). isPlanAvailable === false can be true from changePlanAllowed alone
  // before eligibility returns.
  const eligibilityErrorCode = eligibility.get('errorCode')
  useEffect(() => {
    if (
      eligibilityCheckActive
        && authJwt
        && skuParam
        && skuDeepLinkContentReady
        && isPlanAvailable === false
        && !eligibilityShouldGoToCheckout
        && !changePlanSuccess
        && eligibilityErrorCode
        && eligibilityErrorCode !== USER_REQUIRED
    ) {
      renderModal(TYPE_MODAL_PLAN_ACCESS, {
        hideDismiss: true,
        eligibility,
        page: BILLING_PLAN_GRID_LOCATION_CHANGE_PLAN,
      })
    }

    // No skuParam => no incoming plan row; isPlanAvailable is often undefined, not false.
    // Close plan-access modal left open from a prior ?sku= visit when showing the grid.
    if (eligibilityCheckActive && !skuParam && dialogName === TYPE_MODAL_PLAN_ACCESS) {
      removeModal(TYPE_MODAL_PLAN_ACCESS)
    }
  }, [
    isPlanAvailable,
    renderModal,
    removeModal,
    eligibilityCheckActive,
    eligibilityShouldGoToCheckout,
    authJwt,
    skuParam,
    skuDeepLinkContentReady,
    eligibilityErrorCode,
    changePlanSuccess,
    dialogName,
  ])

  useEffect(() => {
    return () => {
      if (dialogName === TYPE_MODAL_PLAN_ACCESS) {
        removeModal(TYPE_MODAL_PLAN_ACCESS)
      }
    }
  }, [])

  // Redirect to a PI sku
  useEffect(() => {
    const { pathname, query } = location
    const newSku = replaceSkuToPriceIncreasedSku(skuParam)
    const language = user.getIn(['data', 'language'])
    const isLoggedIn = getAuthIsLoggedIn(auth)

    if (newSku !== skuParam) {
      historyRedirect({
        url: pathname,
        query: _assign({}, query, { sku: newSku }),
        history,
        language,
        auth,
        timeout: 0,
      })
      if (!isLoggedIn) setOverlayDialogVisible(TYPE_LOGIN)
    }
  }, [plans, userAccount, auth, skuParam])

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    const profilesReady = userProfiles.get('data', List()).size > 0
    if (profilesReady && !isPrimaryProfile) {
      const language = user.getIn(['data', 'language'])
      return historyRedirect({
        history, url: URL_ACCOUNT, auth, language,
      })
    }
  }, [isPrimaryProfile, userProfiles])

  if (!getAuthIsLoggedIn(auth)) {
    return null
  }

  if (userAccount.get('billingSubscriptionsProcessing')) {
    return null
  }

  const renderContent = () => {
    if (skuParam) {
      if (changePlanSuccess) {
        return null
      }

      // Wait for GET_PLANS_BY_SKU (incoming + current plan) and GET_PLAN_ELIGIBILITY before
      // messages, modal, or change-plan card.
      if (!skuDeepLinkContentReady) {
        return (
          <ChangePlanCardPlaceholder />
        )
      }

      if (isPlanAvailable === false) {
        return (
          <GenericMessage
            message={staticText.get('planNotAvailable')}
          />
        )
      }

      if (isActivePause) {
        return (
          <GenericMessage
            message={staticText.get('accountOnPause')}
            redirectMsg={staticText.get('updatePauseCta')}
            redirecTo={URL_ACCOUNT}
          />
        )
      }

      if (!isUserAccountProcessing && !isDirectPayment) {
        return (
          <GenericMessage
            message={staticText.get('thirdPartyBilling')}
            align={ALIGN_LEFT}
          />
        )
      }

      return (
        <MyAccountChangePlanCard
          sku={skuParam}
          gqlPlans={formattedPlanData}
          currentPlan={currentPlan}
        />
      )
    }

    return (
      <div>
        <p className="my-account-change-plan__description">
          {cancellationScheduled ? staticText.get('continueMembership') : `${staticText.get('plansMeetNeeds')} ${staticText.get('toChangePlan')}`}
        </p>
        <PlanCardGrid
          history={history}
          isAccountChangePlan
          userPlanSubscriptionType={userPlanSubscriptionType}
          shouldScrollToTop
          planGridLocation={BILLING_PLAN_GRID_LOCATION_CHANGE_PLAN}
        />
      </div>
    )
  }

  return (
    <div className="my-account-change-plan">
      <div className="my-account-change-plan__wrapper">
        <BackButton
          className="my-account-change-plan__back-button"
          url={URL_ACCOUNT}
        >
          {staticText.get('backToAccount')}
        </BackButton>
        <H1 as={HEADING_TYPES.H3} className="my-account-change-plan__title">
          {cancellationScheduled ? staticText.get('selectPlan') : staticText.get('changeYourPlan')}
        </H1>
        {renderContent()}
      </div>
    </div>
  )
}

MyAccountChangePlanPage.propTypes = {
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  staticText: ImmutablePropTypes.map.isRequired,
  auth: ImmutablePropTypes.map.isRequired,
  user: ImmutablePropTypes.map.isRequired,
  userAccount: ImmutablePropTypes.map.isRequired,
  userProfiles: ImmutablePropTypes.map,
  paytrack: ImmutablePropTypes.map.isRequired,
  plans: ImmutablePropTypes.map.isRequired,
  renderModal: PropTypes.func.isRequired,
  removeModal: PropTypes.func.isRequired,
  dialog: ImmutablePropTypes.map.isRequired,
}

export default compose(
  connectPage({ seoType: PAGE_SEO_TYPE_LOCATION }),
  connectRedux(
    (state) => ({
      staticText: state.staticText.getIn(['data', 'myAccountChangePlanPage', 'data']),
      auth: state.auth,
      user: state.user,
      userAccount: state.userAccount,
      userProfiles: state.userProfiles,
      paytrack: state.paytrack,
      plans: state.plans,
      dialog: state.dialog,
    }),
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        setOverlayDialogVisible: actions.dialog.setOverlayDialogVisible,
        renderModal: actions.dialog.renderModal,
        removeModal: actions.dialog.removeModal,
      }
    },
  ),
)(MyAccountChangePlanPage)
